/**
 * Admin In-Transit tracking page.
 *
 * Lists shipped-but-undelivered orders with their stored Peninsula
 * tracking (the same ShipmentTrackingEvent rows the customer portal
 * renders), so staff can answer "where is it right now" on the phone
 * seeing exactly what the customer sees. A delivered order drops off
 * automatically (it leaves the SHIPPED query) and surfaces in the
 * in-portal "Delivered Today" badge/list. Read-only: no outbound
 * alerts, no live carrier calls — the Peninsula poller keeps the data
 * fresh.
 *
 * Auto-refreshes every 60 seconds (mirrors PipelineHealth).
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

// Same event shape the customer portal renders (ShipmentTracking.tsx).
interface TrackingEvent {
  event_date: string;
  event_time: string | null;
  activity: string;
  location: string;
}

interface InTransitOrder {
  order_id: number;
  order_number: string;
  customer_name: string;
  pro_number: string;
  peninsula_pro: string;
  ship_date: string | null;
  estimated_delivery: string | null;
  last_tracking_update: string | null;
  awaiting_first_scan: boolean;
  latest_event: TrackingEvent | null;
  events: TrackingEvent[];
  tracking_url: string;
}

interface DeliveredOrder {
  order_id: number;
  order_number: string;
  customer_name: string;
  pro_number: string;
  delivered_at: string | null;
}

interface InTransitData {
  in_transit: InTransitOrder[];
  in_transit_count: number;
  delivered_today: DeliveredOrder[];
  delivered_today_count: number;
  as_of: string;
}

// Mirrors ShipmentTracking.tsx formatDateTime so staff and customer
// read identical timestamps.
const formatDateTime = (dateStr: string, timeStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const dateFormatted = date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  return timeStr ? `${dateFormatted} at ${timeStr}` : dateFormatted;
};

const InTransit: React.FC = () => {
  const [data, setData] = useState<InTransitData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      const response = await api.get('/admin/in-transit/');
      setData(response.data as InTransitData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load in-transit shipments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const toggleExpand = (orderId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  if (loading) {
    return <div style={styles.page}><p style={styles.muted}>Loading in-transit shipments...</p></div>;
  }
  if (error) {
    return <div style={styles.page}><p style={{ color: '#dc2626' }}>{error}</p></div>;
  }
  if (!data) return null;

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.title}>In-Transit Shipments</h1>
        <span style={styles.asOf}>as of {new Date(data.as_of).toLocaleTimeString()}</span>
      </div>

      {/* Delivered Today — in-portal badge + list, no outbound alerts */}
      <div style={styles.deliveredCard}>
        <div style={styles.deliveredHeader}>
          <span style={styles.deliveredBadge}>{data.delivered_today_count}</span>
          <h3 style={{ margin: 0 }}>Delivered Today</h3>
        </div>
        {data.delivered_today_count === 0 ? (
          <p style={styles.muted}>No deliveries yet today.</p>
        ) : (
          <div>
            {data.delivered_today.map(o => (
              <div key={o.order_id} style={styles.deliveredRow}>
                <span style={{ fontWeight: 600 }}>{o.customer_name}</span>
                <span style={styles.muted}>Order {o.order_number} · PRO {o.pro_number}</span>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>
                  ✓ {o.delivered_at ? new Date(o.delivered_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Delivered'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <h3 style={{ margin: '1.5rem 0 0.75rem 0' }}>
        In Transit ({data.in_transit_count})
      </h3>
      {data.in_transit_count === 0 ? (
        <div style={styles.emptyCard}>
          <p style={styles.muted}>Nothing in transit right now — shipped orders with a PRO# will appear here until Peninsula scans them delivered.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {data.in_transit.map(o => {
            const isOpen = expanded.has(o.order_id);
            return (
              <div key={o.order_id} style={styles.card}>
                <button style={styles.cardHeader} onClick={() => toggleExpand(o.order_id)}>
                  <div style={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {o.customer_name}
                      <span style={{ ...styles.muted, fontWeight: 400 }}> · Order {o.order_number} · PRO {o.pro_number}</span>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {o.awaiting_first_scan ? (
                        <span style={styles.awaitingChip}>Awaiting first scan</span>
                      ) : o.latest_event && (
                        <span style={{ fontWeight: 600, color: '#1d4ed8' }}>
                          {o.latest_event.activity}
                          <span style={{ ...styles.muted, fontWeight: 400 }}>
                            {o.latest_event.location ? ` • ${o.latest_event.location}` : ''}
                            {' • '}{formatDateTime(o.latest_event.event_date, o.latest_event.event_time)}
                          </span>
                        </span>
                      )}
                    </div>
                    <div style={{ ...styles.muted, fontSize: 12, marginTop: 2 }}>
                      Shipped {o.ship_date || '—'}
                      {o.estimated_delivery ? ` · est. delivery ${o.estimated_delivery}` : ''}
                    </div>
                  </div>
                  <span style={styles.expandArrow}>{isOpen ? '▾' : '▸'}</span>
                </button>

                {/* Full history — the SAME events the customer sees. */}
                {isOpen && (
                  <div style={styles.history}>
                    {o.events.length === 0 ? (
                      <p style={styles.muted}>No scans yet — the first Peninsula poll after pickup will populate this.</p>
                    ) : (
                      o.events.map((ev, idx) => (
                        <div key={idx} style={styles.timelineItem}>
                          <div style={styles.timelineDotCol}>
                            <div style={{ ...styles.timelineDot, background: idx === 0 ? '#1d4ed8' : '#9ca3af' }} />
                            {idx < o.events.length - 1 && <div style={styles.timelineLine} />}
                          </div>
                          <div style={{ paddingBottom: 10 }}>
                            <div style={{ fontWeight: 600 }}>{ev.activity}</div>
                            <div style={{ ...styles.muted, fontSize: 13 }}>
                              {formatDateTime(ev.event_date, ev.event_time)}
                              {ev.location && ` • ${ev.location}`}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" style={styles.trackLink}>
                      View on Peninsula →
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: { maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 },
  title: { margin: 0, fontSize: '1.5rem' },
  asOf: { color: '#6b7280', fontSize: 13 },
  muted: { color: '#6b7280' },
  deliveredCard: {
    marginTop: '1rem', padding: '1rem',
    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
  },
  deliveredHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  deliveredBadge: {
    minWidth: 32, height: 32, borderRadius: 16, background: '#16a34a', color: 'white',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, padding: '0 8px',
  },
  deliveredRow: {
    display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '6px 0', borderTop: '1px solid #dcfce7',
  },
  emptyCard: {
    padding: '2rem', textAlign: 'center', background: '#f9fafb',
    border: '1px solid #e5e7eb', borderRadius: 10,
  },
  card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '0.9rem 1rem', background: 'none', border: 'none', cursor: 'pointer',
    minHeight: 44, textAlign: 'left', font: 'inherit',
  },
  awaitingChip: {
    padding: '2px 10px', borderRadius: 10, background: '#fef3c7', color: '#92400e',
    fontSize: 13, fontWeight: 600,
  },
  expandArrow: { color: '#6b7280', fontSize: 14, flexShrink: 0 },
  history: { padding: '0 1rem 1rem 1rem', borderTop: '1px solid #f3f4f6' },
  timelineItem: { display: 'flex', gap: 10, paddingTop: 10 },
  timelineDotCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12 },
  timelineDot: { width: 10, height: 10, borderRadius: 5, flexShrink: 0, marginTop: 5 },
  timelineLine: { width: 2, flex: 1, background: '#e5e7eb', marginTop: 2 },
  trackLink: { display: 'inline-block', marginTop: 8, color: '#1d4ed8', fontSize: 14 },
};

export default InTransit;
